import { HTTP_INTERCEPTORS, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MatomoService } from '../services/matomo.service';
import { MatomoApiEvent, apiErrorEvent, apiWriteEvent, parseApiUrl } from '../services/matomo-api';
import { MESURE_MATOMO } from '../services/matomo-mesure';

/**
 * Indicateurs d'activité Matomo de chaque application, au point de passage
 * commun : écritures réussies (Catégorie = application, Action = verbe métier,
 * Nom = objet, cf. `matomo-plan.ts`), téléchargements de fichiers générés (blob,
 * invisibles du suivi des liens) et échecs d'API.
 *
 * L'appelant peut préciser ce que représente la requête (`mesureMatomo`) : une
 * sauvegarde automatique est ignorée, une validation porte son vrai verbe.
 *
 * Enregistré en tête de chaîne pour ne voir que l'issue finale (après
 * rafraîchissement du jeton). N'altère jamais la réponse.
 */
@Injectable()
export class MatomoInterceptor implements HttpInterceptor {
    private readonly matomo = inject(MatomoService);

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const mesure = req.context.get(MESURE_MATOMO);
        if (!this.matomo.enabled || mesure === 'ignorer') {
            return next.handle(req);
        }
        // Application de l'écran au départ de la requête, pas à son retour (navigation entre-temps).
        const application = this.matomo.application;
        return next.handle(req).pipe(tap({
            next: event => {
                if (!(event instanceof HttpResponse)) {
                    return;
                }
                if (req.method === 'GET') {
                    if (req.responseType === 'blob' && parseApiUrl(req.urlWithParams)) {
                        this.matomo.trackDownload(req.urlWithParams);
                    }
                    return;
                }
                const ecriture = apiWriteEvent(req.method, req.url, application);
                this.send(ecriture && mesure ? {
                    ...ecriture,
                    action: mesure.action ?? ecriture.action,
                    name: mesure.nom ?? ecriture.name,
                } : ecriture);
            },
            error: (error: unknown) => {
                if (error instanceof HttpErrorResponse) {
                    this.send(apiErrorEvent(req.method, req.url, error.status, application));
                }
            },
        }));
    }

    private send(event: MatomoApiEvent | null): void {
        if (event) {
            this.matomo.trackEvent(event.category, event.action, event.name);
        }
    }
}

export const matomoInterceptorProviders = [
    { provide: HTTP_INTERCEPTORS, useClass: MatomoInterceptor, multi: true },
];
